/**
 * Короткие подписи комбо и хелперы для ревила / стрипа / очереди (единый источник).
 *
 * @module components/belot/belotComboLabelUi
 */

const CARRE_SHORT_CHIPS = new Set(['4A', '4J', '49', '4X', '4K', '4Q']);

/** @param {string} label */
export function belotComboLabelShort(label) {
  switch (label) {
    case 'TERZ':
      return 'Терц';
    case 'FIFTY':
      return '50';
    case 'HUNDRED':
      return '100';
    case 'CARRE_J':
      return '4J';
    case 'CARRE_9':
      return '49';
    case 'CARRE_A':
      return '4A';
    case 'CARRE_10':
      return '4X';
    case 'CARRE_K':
      return '4K';
    case 'CARRE_Q':
      return '4Q';
    default:
      return typeof label === 'string' && label ? label.slice(0, 4) : '';
  }
}

/** @param {string} key declarationComboKey: `LABEL:ids...` */
export function comboLabelPrefixFromComboKey(key) {
  const s = String(key || '');
  const ix = s.indexOf(':');
  return ix >= 0 ? s.slice(0, ix) : s;
}

/** @param {string} key */
export function isCarreComboKey(key) {
  return /^CARRE_/i.test(comboLabelPrefixFromComboKey(key));
}

/**
 * Режим «карэ» для усиленного ревила (не только 4 карты — у последовательности тоже бывает 4).
 * @param {{ comboLabelRaw?: string, label?: string, cardIds?: number[] }} p
 */
export function isCarreRevealCandidate(p) {
  const raw = String(p?.comboLabelRaw || p?.label || '').trim();
  const ix = raw.indexOf(':');
  const prefix = ix >= 0 ? raw.slice(0, ix) : raw;
  if (/^CARRE_/i.test(prefix)) return true;
  const lab = String(p?.label || '').trim();
  if (CARRE_SHORT_CHIPS.has(lab) && Array.isArray(p?.cardIds) && p.cardIds.length === 4) return true;
  return false;
}

/**
 * Терц: ровно 3 карты, префикс ключа TERZ, не ветка карэ.
 * @param {{ comboLabelRaw?: string, label?: string, cardIds?: number[] }} p
 */
export function isTerzRevealCandidate(p) {
  if (isCarreRevealCandidate(p)) return false;
  const raw = String(p?.comboLabelRaw || '').trim();
  const ix = raw.indexOf(':');
  const prefix = ix >= 0 ? raw.slice(0, ix) : raw;
  if (prefix !== 'TERZ') return false;
  const ids = Array.isArray(p?.cardIds) ? p.cardIds : [];
  return ids.length === 3;
}

/**
 * Ключ `declarationComboKey`: `LABEL:id1,id2,...`.
 * @param {string} key
 * @returns {{ label: string, labelRaw: string, cardIds: number[] }}
 */
export function parseDeclaredComboKey(key) {
  const s = String(key);
  const ix = s.indexOf(':');
  const labelRaw = ix >= 0 ? s.slice(0, ix) : s;
  const label = belotComboLabelShort(labelRaw);
  let cardIds = [];
  if (ix >= 0) {
    cardIds = s
      .slice(ix + 1)
      .split(',')
      .map((x) => Number(String(x).trim()))
      .filter((n) => Number.isInteger(n) && n >= 0);
  }
  return { label, labelRaw, cardIds };
}

/**
 * В ключе есть карты для показа лицом в ревиле (casual declare, все места включая героя).
 * @param {number[]} cardIds
 */
export function hasRevealableDeclaredComboCardIds(cardIds) {
  if (!Array.isArray(cardIds) || cardIds.length === 0) return false;
  return cardIds.every((n) => Number.isInteger(n) && n >= 0);
}

/**
 * Текст золотого чипа над веером.
 * @param {{ comboLabelRaw?: string, label?: string }} opts
 */
export function revealChipTextFromOpts(opts) {
  if (opts?.comboLabelRaw) {
    const seg = String(opts.comboLabelRaw).split(':')[0];
    return belotComboLabelShort(seg);
  }
  const lab = String(opts?.label || '');
  const seg = lab.split(':')[0];
  if (seg.startsWith('CARRE_') || belotComboLabelShort(seg) !== seg.slice(0, 4)) {
    return belotComboLabelShort(seg);
  }
  return belotComboLabelShort(lab);
}

/** @param {string} key */
function cardCountFromComboKey(key) {
  const s = String(key);
  const ix = s.indexOf(':');
  if (ix < 0) return 0;
  return s
    .slice(ix + 1)
    .split(',')
    .map((x) => String(x).trim())
    .filter(Boolean).length;
}

/** Приоритет ревила: выше — показываем раньше в очереди. */
const PREFIX_WEIGHT = {
  CARRE_J: 520,
  CARRE_9: 410,
  CARRE_A: 300,
  CARRE_10: 300,
  CARRE_K: 300,
  CARRE_Q: 300,
  HUNDRED: 350,
  FIFTY: 240,
  TERZ: 120,
};

function prefixScore(prefix) {
  return PREFIX_WEIGHT[prefix] ?? 0;
}

/** Сравнение для сортировки: b сильнее a → отрицательное. */
export function compareComboKeysRevealDesc(a, b) {
  const pa = comboLabelPrefixFromComboKey(a);
  const pb = comboLabelPrefixFromComboKey(b);
  const sa = prefixScore(pa) + cardCountFromComboKey(a) * 2;
  const sb = prefixScore(pb) + cardCountFromComboKey(b) * 2;
  return sb - sa;
}

/** @param {string[]} keys */
export function sortDeclaredComboKeysForRevealDescending(keys) {
  if (!Array.isArray(keys)) return [];
  return [...keys].sort(compareComboKeysRevealDesc);
}
